import React, { useState, useEffect } from 'react';
import { SquareStack } from 'lucide-react';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';
import './style.css';
import ExploreChangesButton from '../ExploreChangesButton/ExploreChangesButton';
import DatapointHistory from '../DatapointHistory/DatapointHistory';

const DatapointListView = ({
    datapointTimelines,
    projectTimelines,
    changeLog,
    datapoints,
    projects,
    menuSections
}) => {
    const [filterType, setFilterType] = useState('all'); // 'all' or 'individual'
    const [selectedFilters, setSelectedFilters] = useState([]); // array of selected filter values
    const [datapointHistoryItem, setDatapointHistoryItem] = useState(null);


    // Helper to get the latest change for a timeline
    const getLatestChange = (timeline) => {
        if (!timeline || timeline.length === 0) return null;
        return timeline[timeline.length - 1];
    };

    // Helper function to check if a section has any changes
    const sectionHasChanges = (sectionData) => {
        if (!Array.isArray(sectionData)) return false;

        const sectionDatapointNames = sectionData.map(item => item.datapoint);

        return datapointTimelines.some(timeline =>
            sectionDatapointNames.includes(timeline.datapoint_name)
        );
    };

    const handleFilterClick = (filterValue) => {
        if (filterValue === 'all') {
            setFilterType('all');
            setSelectedFilters([]);
        } else {
            // If coming from 'all', start fresh with just this filter
            if (filterType === 'all') {
                setSelectedFilters([filterValue]);
            } else {
                // Toggle the selected filter
                setSelectedFilters(prev => {
                    if (prev.includes(filterValue)) {
                        // Remove the filter if it's already selected
                        const newFilters = prev.filter(f => f !== filterValue);
                        // If no filters left, switch back to 'all'
                        if (newFilters.length === 0) {
                            setFilterType('all');
                            return [];
                        }
                        return newFilters;
                    } else {
                        // Add the new filter
                        return [...prev, filterValue];
                    }
                });
            }
            setFilterType('individual');
        }
    };

    // Create filter options with their associated data
    const filterOptions = [
        {
            value: 'all',
            label: 'All Changes',
            icon_light: menuSections[0]?.icon_light,
            icon_dark: menuSections[0]?.icon_dark,
            sections: menuSections.map(section => ({
                ...section,
                items: section.options.map(option => ({
                    ...option,
                    timelines: option.id === 'completed' ?
                        projectTimelines :
                        datapointTimelines.filter(timeline =>
                            option.data.some(item => item.datapoint === timeline.datapoint_name)
                        )
                })).filter(option => option.timelines.length > 0)
            })).filter(section => section.items.length > 0)
        },
        ...menuSections.flatMap(section =>
            section.options
                .filter(option => {
                    if (section.id === 'resilience' && option.id === 'completed') {
                        return projectTimelines && projectTimelines.length > 0;
                    }
                    return sectionHasChanges(option.data);
                })
                .map(option => ({
                    value: `${section.id}-${option.id}`,
                    label: option.label,
                    icon_light: section.icon_light,
                    icon_dark: section.icon_dark,
                    sectionId: section.id,
                    sections: [{
                        ...section,
                        items: [{
                            ...option,
                            timelines: option.id === 'completed' ?
                                projectTimelines :
                                datapointTimelines.filter(timeline =>
                                    option.data.some(item => item.datapoint === timeline.datapoint_name)
                                )
                        }]
                    }]
                }))
        ).filter(Boolean)
    ];

    // Get filtered sections using filterOptions
    const getFilteredSections = () => {
        if (filterType === 'all') {
            return filterOptions[0].sections;
        }

        return selectedFilters.flatMap(filterValue => {
            const filter = filterOptions.find(option => option.value === filterValue);
            return filter ? filter.sections : [];
        });
    };

    const filteredSections = getFilteredSections();
    const hasFilterOptions = filterOptions.length > 1;

    // Ensure projects is an array
    const projectsArray = Array.isArray(projects) ? projects : [];

    const getItemName = (timeline) => {
        if (timeline.project_id) {
            return projects[timeline.project_id]?.strategy_name || timeline.project_id;
        }
        return datapoints.find(d => d.datapoint === timeline.datapoint_name)?.headline || timeline.datapoint_name;
    };

    const getLatestAction = (timeline) => {
        if (!timeline.timeline || timeline.timeline.length === 0) return '';

        const lastChange = timeline.timeline[timeline.timeline.length - 1];

        if (timeline.project_id) {
            // Handle project changes
            return lastChange.added_or_removed === 'removed' ?
                'Marked as incomplete' :
                'Marked as complete';
        } else {
            // Handle datapoint changes
            if (lastChange.updated_or_removed === 'removed') {
                return 'Removed';
            } else {
                return `Changed to ${lastChange.updated_to}`;
            }
        }
    };

    console.log(datapointHistoryItem);
    console.log(datapoints)

    const handleExploreClick = (sectionIndex, itemIndex, timelineIndex) => {
        const section = filteredSections[sectionIndex];
        if (!section) return;

        const item = section.items[itemIndex];
        if (!item) return;

        const timeline = item.timelines[timelineIndex];
        if (!timeline) return;

        // Find matching datapoint to get headline
        const matchingDatapoint = datapoints.find(
            dp => dp.datapoint === timeline.datapoint_name
        );

        const historyItem = {
            icons: {
                dark: section.icon_dark,
                light: section.icon_light
            },
            section_label: section.title,
            sub_section_label: item.label,
            timeline: timeline,
            data_type: matchingDatapoint?.data_type || null,
            headline: matchingDatapoint?.headline || timeline.datapoint_name
        };

        setDatapointHistoryItem(historyItem);
    };

    const handleReturnToDatapoints = () => {
        setDatapointHistoryItem(null);
    };

    return (
        <div className="datapoint-list-view">
            {hasFilterOptions && (
                <div className="filter-bar">
                    {filterOptions.map(filter => {
                        const isSelected = filterType === 'all' ?
                            filter.value === 'all' :
                            selectedFilters.includes(filter.value);

                        return (
                            <button
                                key={filter.value}
                                className={`filter-button ${isSelected ? 'active' : ''}`}
                                onClick={() => handleFilterClick(filter.value)}
                            >
                                <img
                                    src={CreateCloudfrontUrl(isSelected ? filter.icon_light : filter.icon_dark)}
                                    alt=""
                                    className="filter-icon"
                                />
                                {filter.label}
                            </button>
                        );
                    })}
                </div>
            )}

            {datapointHistoryItem ? (
                <DatapointHistory historyItem={datapointHistoryItem} returnTo="Datapoint List" returnToFunction={handleReturnToDatapoints} />
            ) : (
                <div className="section-items">
                    {filteredSections.flatMap((section, sectionIndex) =>
                        section.items.flatMap((item, itemIndex) =>
                            item.timelines.map((timeline, timelineIndex) => (
                                <div
                                    key={timeline.datapoint_name || timeline.project_id}
                                    className="list-view-change-card"
                                >
                                    <div className="list-view-change-card-header">
                                        <div className="list-view-change-card-icon-container">
                                            <img
                                                src={CreateCloudfrontUrl(section.icon_dark)}
                                                alt=""
                                            />
                                        </div>
                                        <h3 className="list-view-change-title">
                                            {getItemName(timeline)}
                                        </h3>
                                    </div>

                                    <div className="list-view-change-stats">
                                        <div className="list-view-stat-group">
                                            <label>Total Changes</label>
                                            <span>{timeline.timeline.length}</span>
                                        </div>
                                        <div className="list-view-stat-group">
                                            <label>Latest Action</label>
                                            <span>{getLatestAction(timeline)}</span>
                                        </div>
                                    </div>

                                    {timeline.timeline.length > 0 && (
                                        <ExploreChangesButton
                                            timelineLength={timeline.timeline.length}
                                            type={timeline.project_id ? 'project' : 'datapoint'}
                                            onClick={() => handleExploreClick(sectionIndex, itemIndex, timelineIndex)}
                                        />
                                    )}
                                </div>
                            ))
                        )
                    )}
                </div>
            )}
        </div>
    );
};

export default DatapointListView;
